import { Box, Button, Group, Paper, Skeleton, Table, Text, UnstyledButton, VisuallyHidden, Anchor } from '@mantine/core';
import type { StaffCapability } from '@motionsafe/ms-console-api/admin/v1/user_pb.js';
import { ConsoleRole, StaffRole, UserStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IconArrowRight, IconCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';

import { errorText, meQuery, staffRolesQuery, userCountsQuery } from '../../api/hooks';
import { usersIn } from '../../lib/counts';
import { PageHeader } from '../../components/page';
import { isStaff, STAFF_ROLE_ORDER, staffRoleName } from '../../lib/roles';
import { Route as AppRoute } from '../_app';
import type { StaffFilter } from './people';

// What each member of MotionSafe staff can change. The band at the top is
// the page's answer at a glance: every role with how much of the console it
// may change, as a fraction of every permission there is, what it is for,
// and who holds it. Under it, one table of every permission by area, a
// column per role that changes anything. Read only changes nothing, so it
// has no column; what no staff role allows is gathered at the end. The last
// column is an organization's own administrators, for their organization,
// so the two kinds of admin are never read as one. Every word of the table
// is the service's own (UserService.ListStaffRoles), the rule it refuses by.
export const Route = createFileRoute('/_app/staff-roles')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: StaffRoles,
});

/** A column of the table: a MotionSafe admin, a staff role, or an organization's administrator. */
type Column = 'admin' | StaffRole | 'org_admin';
const COLUMNS: Column[] = ['admin', ...STAFF_ROLE_ORDER, 'org_admin'];
const columnName = (c: Column) => (c === 'admin' ? 'MotionSafe admin' : c === 'org_admin' ? 'Org admin' : staffRoleName(c));
const allows = (p: StaffCapability, c: Column) => c === 'admin' || (c === 'org_admin' ? p.orgAdmin : p.roles.includes(c));
// Every other role column is striped, so each reads straight down the table.
const striped = (c: Column) => COLUMNS.indexOf(c) % 2 === 0 || undefined;

/** The last group: what no staff role allows. */
const ADMINS_ONLY = 'No staff role';

/** The table's groups in the service's order, every permission no role allows moved to the end. */
function groupsOf(capabilities: StaffCapability[]): { title: string; rows: StaffCapability[] }[] {
  const groups: { title: string; rows: StaffCapability[] }[] = [];
  for (const c of capabilities.filter((x) => x.roles.length > 0)) {
    const last = groups.find((g) => g.title === c.group);
    if (last) last.rows.push(c); else groups.push({ title: c.group, rows: [c] });
  }
  const adminsOnly = capabilities.filter((x) => x.roles.length === 0);
  return adminsOnly.length ? [...groups, { title: ADMINS_ONLY, rows: adminsOnly }] : groups;
}

interface Band { column: Column | null; filter: StaffFilter; name: string; sentence: string | undefined; held: number | undefined; allowed: number | undefined }

// The People page's filter for each role in the band.
const FILTER_OF: Record<StaffRole, StaffFilter> = { [StaffRole.UNSPECIFIED]: 'read_only', [StaffRole.SUPPORT]: 'support', [StaffRole.FULFILLMENT]: 'fulfillment', [StaffRole.ACCOUNTS]: 'accounts' };

function StaffRoles() {
  const { me } = AppRoute.useRouteContext();
  const table = useQuery(staffRolesQuery);
  // Staff are MotionSafe's own people, so the counts are its People page's.
  const people = useQuery(userCountsQuery(me.orgId));
  // A role chosen in the band, or by its heading, is lit down the table.
  const [focus, setFocus] = useState<Column | null>(null);
  const toggle = (c: Column) => setFocus(focus === c ? null : c);

  const capabilities = table.data?.capabilities;
  const total = capabilities?.length;
  const standing = (people.data ?? []).filter((u) => u.status !== UserStatus.DEPROVISIONED);
  const count = (held: (role: ConsoleRole, roles: StaffRole[]) => boolean) => (people.data ? usersIn(standing, (u) => held(u.consoleRole, u.staffRoles)) : undefined);
  const summary = (r: StaffRole) => table.data?.roles.find((x) => x.role === r)?.summary;
  const band: Band[] = [
    { column: 'admin', filter: 'admin', name: 'MotionSafe admin', sentence: 'Changes anything, for every organization, including keys, limits, and staff.', held: count((c) => c === ConsoleRole.MS_SUPERADMIN), allowed: total },
    ...STAFF_ROLE_ORDER.map((r) => ({ column: r, filter: FILTER_OF[r], name: staffRoleName(r), sentence: summary(r), held: count((c, roles) => c === ConsoleRole.MS_STAFF && roles.includes(r)), allowed: capabilities?.filter((p) => p.roles.includes(r)).length })),
    { column: null, filter: 'read_only' as const, name: 'Read only', sentence: 'Sees every organization, its records, and its audit trail, and changes nothing.', held: count((c, roles) => c === ConsoleRole.MS_STAFF && roles.length === 0), allowed: total === undefined ? undefined : 0 },
  ];
  const groups = capabilities ? groupsOf(capabilities) : [];

  return (
    <>
      <PageHeader
        title="Staff roles"
        description="What each member of MotionSafe staff can change. Everyone on staff sees every organization; a person can hold several roles, and an admin's own role is changed by another admin. The last column is what an organization's own administrators can change, for their organization only."
      />

      {/* The band: a ruled row, a cell per role, each a measure of how much of the console it may change. */}
      <Box component="ul" className="role-band" aria-label="Roles">
        {band.map((b) => <BandCell key={b.name} band={b} orgId={me.orgId} total={total} focused={b.column !== null && focus === b.column} onPick={b.column === null ? undefined : () => toggle(b.column!)} loadingHeld={!people.isError} loadingRoles={!table.isError} />)}
      </Box>
      {people.isError && (
        <Group gap="sm" mt="md">
          <Text size="sm" c="danger">Could not load who holds each role: {errorText(people.error)}.</Text>
          <Button variant="subtle" size="compact-sm" onClick={() => void people.refetch()} disabled={people.isFetching}>Try again</Button>
        </Group>
      )}

      <Paper p={0} mt="xl" style={{ overflow: 'hidden' }}>
        <Table.ScrollContainer minWidth={720} type="native">
          <Table layout="fixed" className="roles-table" aria-label="What each staff role can change" aria-busy={table.isPending}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className="roles-pin">Permission</Table.Th>
                {COLUMNS.map((c) => (
                  <Table.Th key={c} w={{ base: 96, sm: 116 }} ta="center" data-stripe={striped(c)} data-focus={focus === c || undefined}>
                    <UnstyledButton className="roles-head" aria-pressed={focus === c} onClick={() => toggle(c)}>
                      {columnName(c)}
                    </UnstyledButton>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {table.isPending && [0, 1, 2, 3, 4, 5].map((i) => (
                <Table.Tr key={i} aria-hidden="true">
                  <Table.Td><Skeleton height={12} width={`${60 - i * 5}%`} /></Table.Td>
                  {COLUMNS.map((c) => <Table.Td key={c} data-stripe={striped(c)} />)}
                </Table.Tr>
              ))}
              {groups.map((g) => <Rows key={g.title} title={g.title} rows={g.rows} focus={focus} />)}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {table.isError && (
          <Group gap="sm" justify="center" py="xl" px="lg">
            <Text size="sm" c="danger">Could not load the roles: {errorText(table.error)}.</Text>
            <Button variant="subtle" size="compact-sm" onClick={() => void table.refetch()} disabled={table.isFetching}>Try again</Button>
          </Group>
        )}
      </Paper>
      <Text size="xs" c="dimmed" mt="sm" maw={640}>
        Roles are given on <Anchor inherit renderRoot={(p) => <Link {...p} to="/people" search={{ org: me.orgId }} />}>MotionSafe&rsquo;s People page</Anchor>, from a person&rsquo;s menu. A change takes effect from their next action.
      </Text>
    </>
  );
}

// One role in the band: its name, how much it may change as a fraction of
// every permission with a ruled meter under it, and what it is for; a role
// with a column is a button that lights that column below. At its foot, who
// holds it, as a link to them on the People page.
function BandCell({ band, orgId, total, focused, onPick, loadingHeld, loadingRoles }: { band: Band; orgId: string; total: number | undefined; focused: boolean; onPick: (() => void) | undefined; loadingHeld: boolean; loadingRoles: boolean }) {
  const share = total && band.allowed !== undefined ? band.allowed / total : 0;
  const body = (
    <>
      <Text fz={17} fw={600} lh={1.3}>{band.name}</Text>
      <Text className="num" mt={10} lh={1.1}>
        {band.allowed === undefined || total === undefined
          ? (loadingRoles ? <Skeleton component="span" height={22} width={72} display="inline-block" /> : null)
          : <><Text span fz={28} fw={600} lh={1}>{band.allowed}</Text><Text span size="sm" c="dimmed"> of {total} permissions</Text></>}
      </Text>
      <Box className="role-meter" mt={10} aria-hidden="true"><Box className="role-meter-fill" style={{ width: `${share * 100}%` }} /></Box>
      {band.sentence === undefined
        ? (loadingRoles ? <><Skeleton height={10} mt={12} width="90%" /><Skeleton height={10} mt={6} width="70%" /></> : null)
        : <Text fz={13} c="dimmed" mt={12} lh={1.5}>{band.sentence}</Text>}
    </>
  );
  const people = band.held === 1 ? '1 person' : `${band.held ?? 0} people`;
  return (
    <Box component="li" className="role-band-cell" data-focus={focused || undefined}>
      {onPick
        ? <UnstyledButton className="role-band-pick" aria-pressed={focused} aria-label={`${band.name}: show in the table`} onClick={onPick}>{body}</UnstyledButton>
        : <Box className="role-band-pick">{body}</Box>}
      <Box className="role-band-foot">
        {band.held === undefined
          ? (loadingHeld ? <Skeleton height={10} width={72} my={4} /> : null)
          : (
            <Anchor size="sm" className="num" aria-label={`${people}: ${band.name}, on the People page`} renderRoot={(p) => <Link {...p} to="/people" search={{ org: orgId, role: band.filter }} />}>
              <Group component="span" gap={4} wrap="nowrap">{people}<IconArrowRight size={14} stroke={2} aria-hidden="true" /></Group>
            </Anchor>
          )}
      </Box>
    </Box>
  );
}

// One area of the table under its label: a check where a role allows the
// permission, and a word for a screen reader where it does not.
function Rows({ title, rows, focus }: { title: string; rows: StaffCapability[]; focus: Column | null }) {
  return (
    <>
      <Table.Tr className="roles-group">
        <Table.Th scope="rowgroup" colSpan={1 + COLUMNS.length}>{title}</Table.Th>
      </Table.Tr>
      {rows.map((p) => (
        <Table.Tr key={p.label}>
          <Table.Th scope="row" fw={400} className="roles-pin">
            <Text size="sm">{p.label}</Text>
            {p.detail && <Text size="xs" c="dimmed" mt={2} lh={1.5}>{p.detail}</Text>}
          </Table.Th>
          {COLUMNS.map((c) => (
            <Table.Td key={c} ta="center" data-stripe={striped(c)} data-focus={focus === c || undefined}>
              {allows(p, c)
                ? <IconCheck size={18} stroke={2} color="var(--mantine-color-success-text)" role="img" aria-label={`${columnName(c)} can`} style={{ display: 'block', marginInline: 'auto' }} />
                : <VisuallyHidden>{columnName(c)} cannot</VisuallyHidden>}
            </Table.Td>
          ))}
        </Table.Tr>
      ))}
    </>
  );
}
